/**
 * Shared bits for the loop-conversation sections (open questions, revision
 * requests, involvement responses): an inline text composer that shows the
 * server's refusal in place, and a button that states why it is disabled.
 */

import { useState, type ReactNode } from 'react';
import { AlertCircle } from 'lucide-react';
import type { ActionGate } from './threadLogic';

import { formatWhen } from '../../../../lib/calendarDate';
export const SECTION_CLASS =
  'border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] px-5 py-4 space-y-3';

export const PRIMARY_BUTTON_CLASS =
  'inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 min-h-[36px] border border-[var(--slate)] text-[var(--text-sec)] bg-[var(--slate-dim)] hover:bg-[var(--slate-dim)] disabled:opacity-50 disabled:cursor-not-allowed';

export const QUIET_BUTTON_CLASS =
  'inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 min-h-[36px] border border-[var(--rule)] text-[var(--text-sec)] bg-[var(--bg-raised)] hover:bg-[var(--surface)] disabled:opacity-50 disabled:cursor-not-allowed';

// The "when" formatter lives in lib/calendarDate; re-exported here for the
// callers that always imported it from this file.
// eslint-disable-next-line react-refresh/only-export-components
export { formatWhen };

/** A button that, when the gate refuses, is disabled AND says why underneath. */
export function GatedButton({
  gate,
  busy = false,
  onClick,
  className = QUIET_BUTTON_CLASS,
  children,
}: {
  gate: ActionGate;
  busy?: boolean;
  onClick: () => void;
  className?: string;
  children: ReactNode;
}) {
  return (
    <span className="inline-flex flex-col items-start gap-1">
      <button
        type="button"
        onClick={onClick}
        disabled={!gate.allowed || busy}
        title={gate.allowed ? undefined : gate.reason}
        aria-disabled={!gate.allowed || busy}
        className={className}
      >
        {children}
      </button>
      {!gate.allowed && gate.reason && (
        <span className="text-[11px] leading-snug text-[var(--text-ter)] max-w-xs">{gate.reason}</span>
      )}
    </span>
  );
}

export function InlineError({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <p role="alert" className="flex items-start gap-1.5 text-xs text-[var(--coral)]">
      <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
      <span>{message}</span>
    </p>
  );
}

/**
 * Textarea + submit. `onSubmit` must throw on failure; the text stays in the
 * box and the thrown message (the server's) is shown. It only clears on success.
 */
export function ThreadComposer({
  label,
  placeholder,
  submitLabel,
  busyLabel,
  required = true,
  onSubmit,
  onCancel,
}: {
  label: string;
  placeholder?: string;
  submitLabel: string;
  busyLabel: string;
  /** When false an empty submission is allowed (e.g. an optional closing note). */
  required?: boolean;
  onSubmit: (text: string) => Promise<void>;
  onCancel?: () => void;
}) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const empty = text.trim() === '';

  const submit = async () => {
    if (busy || (required && empty)) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit(text.trim());
      setText('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The server refused.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      <label className="block text-xs font-medium text-[var(--text-sec)]">
        {label}
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={placeholder}
          rows={3}
          disabled={busy}
          className="mt-1 block w-full border border-[var(--rule)] bg-[var(--bg)] dark:bg-[var(--surface)] px-3 py-2 text-sm text-[var(--text-pri)] placeholder:text-[var(--text-ter)] focus:outline-none focus:border-[var(--slate)]"
        />
      </label>
      <InlineError message={error} />
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => void submit()}
          disabled={busy || (required && empty)}
          title={required && empty ? `${label} is required.` : undefined}
          className={PRIMARY_BUTTON_CLASS}
        >
          {busy ? busyLabel : submitLabel}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} disabled={busy} className={QUIET_BUTTON_CLASS}>
            Cancel
          </button>
        )}
        {required && empty && (
          <span className="text-[11px] text-[var(--text-ter)]">{label} is required.</span>
        )}
      </div>
    </div>
  );
}
